import { filterObjByArray } from '@/utils/filterInput';
import {
  UPDATE_ME_FEDERATED_INPUT,
  GET_MY_USER,
  UPDATE_ME_FEDERATED,
} from '@/modules/app/MyUserSchema';
import { assign, AnyEventObject, MachineOptions } from 'xstate';
import { AuthContext, defaultContext } from './authMachine';
import { FetchPolicy } from '@apollo/client';
import { getCachePersistor, isEffectivelyOnline } from '@bilderit/offline-link';
import { resolveCurrentIdentity } from './currentIdentity';
import { resetPostHogIdentity } from './analytics/posthog';
import {
  forgetAuthDestination,
  rememberAuthDestination,
} from './authDestination';

const COGNITO_ATTRIBUTES = [
  'address',
  'birthdate',
  'email',
  'family_name',
  'gender',
  'given_name',
  'locale',
  'middle_name',
  'name',
  'nickname',
  'phone_number',
  'picture',
  'preferred_username',
  'profile',
  'updated_at',
  'website',
  'zoneinfo',
];

///////////////
// SERVICES
///////////////

export const getUser = async (
  context: Pick<AuthContext, 'GraphQLClient' | 'authData'>,
  event?: AnyEventObject,
  fetchPolicy: FetchPolicy = isEffectivelyOnline('3g')
    ? 'cache-first'
    : 'cache-only'
) => {
  try {
    const { data, errors } = await context.GraphQLClient.query({
      query: GET_MY_USER,
      fetchPolicy,
    });
    if (data.getMe && data.getMe.__typename.endsWith('Error')) throw data.getMe;
    if (errors) throw errors;
    const me = data.getMe.item;

    const expected = context.authData
      ?.getSignInUserSession()
      ?.getIdToken()
      ?.payload?.email?.trim()
      .toLowerCase();
    const actual = me?.email?.trim().toLowerCase();

    if (expected && actual && expected !== actual) {
      // The cached getMe doesn't match the signed-in token's identity. When
      // online, the network getMe is the answer: the server resolves the user
      // by the token's identity, and the user's email can legitimately differ
      // from the token's (e.g. Cognito already confirmed a new email the
      // database doesn't have yet). Offline, fail closed.
      if (isEffectivelyOnline('3g')) {
        const network = await context.GraphQLClient.query({
          query: GET_MY_USER,
          fetchPolicy: 'network-only',
        });
        if (
          network.data.getMe &&
          network.data.getMe.__typename.endsWith('Error')
        )
          throw network.data.getMe;
        if (network.errors) throw network.errors;
        return network.data.getMe.item;
      }
      context.GraphQLClient.cache.evict({
        id: 'ROOT_QUERY',
        fieldName: 'getMe',
      });
      context.GraphQLClient.cache.gc();
      throw new Error(
        'Identidad en caché no coincide con la sesión actual; se descartó el usuario cacheado.'
      );
    }
    return me;
  } catch (e) {
    console.error('error while getting user');
    console.error(e);
    context.GraphQLClient.cache.evict({ id: 'ROOT_QUERY', fieldName: 'getMe' });
    context.GraphQLClient.cache.gc();
    throw e;
  }
};

const options: Partial<MachineOptions<AuthContext, any>> = {
  delays: {
    SIGNIN_THROTTLE_DELAY: (context) => context.signinThrottleDelay,
  },
  services: {
    noop: () => new Promise<void>((resolve) => resolve()),
    checkAuthenticatedUser: async (context) => {
      try {
        const cachedUser = await context.Auth.currentAuthenticatedUser({
          bypassCache: false,
        });
        return cachedUser;
      } catch {
        const networkUser = await context.Auth.currentAuthenticatedUser();
        return networkUser;
      }
    },
    signIn: async (context, { data: { username, password } }) =>
      await context.Auth.signIn(username, password),
    submitProfileData: async (context, { data }) => {
      return context.GraphQLClient.mutate({
        mutation: UPDATE_ME_FEDERATED,
        variables: { input: filterObjByArray(UPDATE_ME_FEDERATED_INPUT, data) },
        fetchPolicy: 'no-cache',
      });
    },
    signUp: async (
      context,
      { data: { username, password, email, attributes } }
    ) => {
      const attrs = Object.keys(attributes).reduce((acc: any, v) => {
        if (COGNITO_ATTRIBUTES.includes(v)) acc[v] = attributes[v];
        else acc[`custom:${v}`] = attributes[v];
        return acc;
      }, {});
      if (!import.meta.env.VITE_APP_ENTRY) throw new Error('env var not set');
      return context.Auth.signUp({
        username,
        password,
        attributes: { email, ...attrs },
        clientMetadata: {
          app: import.meta.env.VITE_APP_ENTRY?.replace(
            'maquinista',
            'operario'
          ),
        },
      });
    },
    forgotPassword: async (context) => {
      if (!context.forgotPasswordUsername) throw new Error('username unknown');
      if (!import.meta.env.VITE_APP_ENTRY) throw new Error('env var not set');
      return context.Auth.forgotPassword(context.forgotPasswordUsername, {
        app: import.meta.env.VITE_APP_ENTRY?.replace('maquinista', 'operario'),
      });
    },
    forgotPasswordSubmit: async (context) => {
      const {
        forgotPasswordUsername,
        forgotPasswordCode,
        forgotPasswordNewPassword,
      } = context;
      if (
        forgotPasswordUsername &&
        forgotPasswordCode &&
        forgotPasswordNewPassword
      ) {
        return context.Auth.forgotPasswordSubmit(
          forgotPasswordUsername,
          forgotPasswordCode,
          forgotPasswordNewPassword
        );
      }
      throw new Error('missing required fields');
    },
    confirmEmail: async (context) => {
      let userName = context.username;
      if (!context.username) userName = context.authData?.getUsername();
      if (!userName) throw new Error('username unknown');
      if (!context.emailConfirmationCode)
        throw new Error('confirmation code unknown');
      return context.Auth.confirmSignUp(
        userName,
        context.emailConfirmationCode
      );
    },
    resendConfirmation: async (context) => {
      let userName = context.username;
      if (!context.username) userName = context.authData?.getUsername();
      if (!userName) throw new Error('username unknown');
      if (!import.meta.env.VITE_APP_ENTRY) throw new Error('env var not set');
      return context.Auth.resendSignUp(userName, {
        app: import.meta.env.VITE_APP_ENTRY?.replace('maquinista', 'operario'),
      });
    },
    logIn: (context: AuthContext, event: AnyEventObject) =>
      getUser(context, event),
  },
  actions: {
    resetDelay: assign({
      signinThrottleDelay: (context) => context.signinThrottleInitialDelay,
    }),
    increaseDelay: assign({
      signinThrottleDelay: (context) =>
        context.signinThrottleDelay * context.signinThrottleFactor,
    }),
    clearContext: assign(defaultContext),
    signOut: async (context, event) => {
      const identity = await resolveCurrentIdentity();
      await context.GraphQLClient.stop();
      await context.GraphQLClient.resetStore();
      if (identity) {
        await getCachePersistor(context.GraphQLClient, identity).purge();
      }
      await context.Auth.signOut();
      (window as any).$chatwoot?.reset();
      // Flushes the user's queued analytics, resets the identity and drops
      // anonymous leftovers — see resetPostHogIdentity.
      await resetPostHogIdentity();
      !event.data?.dontReload && window.location.reload();
    },
    setError: assign({
      error: (context, event) => event.data,
    }),
    clearError: assign({
      error: undefined,
    }),
    saveDestination: assign({
      destination: (context) =>
        context.Navigator.location.pathname +
        context.Navigator.location.search +
        context.Navigator.location.hash,
    }),
    clearDestination: assign({
      destination: undefined,
    }),
    // The machine does not outlive the sign-in (authDestination.ts).
    rememberDestination: (context) =>
      context.destination && rememberAuthDestination(context.destination),
    forgetDestination: () => forgetAuthDestination(),
    redirectToLogin: (context) =>
      context.Navigator.history.push(`/login${window.location.search}`),
    redirectToDestination: (context) =>
      context.destination &&
      context.Navigator.history.push(context.destination),
    loading: assign({ loading: true }),
    done: assign({ loading: false }),
    setEmailConfirmationDestination: assign({
      emailConfirmationDestination: (context, event) =>
        event.data.codeDeliveryDetails?.Destination,
    }),
    assignEmailConfirmationDestination: assign({
      emailConfirmationDestination: (context, event) =>
        event.data.CodeDeliveryDetails.Destination,
    }),
    assignConfirmationCode: assign({
      emailConfirmationCode: (context, event) => event.data.code,
    }),
    assignForgotPasswordInputs: assign({
      forgotPasswordUsername: (context, event) => event.data.username,
      forgotPasswordCode: (context, event) => event.data.code,
      forgotPasswordNewPassword: (context, event) => event.data.new_password,
    }),
    assignForgotPasswordUsernameAsUsername: assign({
      username: (context) => context.forgotPasswordUsername,
    }),
    assignForgotPasswordDelivery: assign({
      forgotPasswordDelivery: (context: AuthContext, event: AnyEventObject) =>
        event.data.CodeDeliveryDetails,
    }),
    assignSignInInputs: assign({
      username: (context, event) => event.data.username,
      password: (context, event) => event.data.password,
    }),
    assignSignUpInputs: assign({
      username: (context, event) => event.data.username,
      password: (context, event) => event.data.password,
      email: (context, event) => event.data.email,
      attributes: (context, event) => event.data.attributes,
    }),
    assignAuthData: assign({
      authData: (context, event) => event.data,
    }),
    assignUser: assign({
      user: (context, event) => event.data,
    }),
    assignPartialProfile: assign({
      partialProfile: (context, event) => event.data.partial_profile,
    }),
  },
  guards: {
    isUserNotConfirmedException: (context, event) =>
      event.data.code === 'UserNotConfirmedException',
    isUnverifiedEmailException: (context, event) =>
      event.data.code === 'InvalidParameterException',
    isNetworkError: (context, event) => event.data.__typename === 'ServerError',
    isMissingData: (context, event) =>
      event.data.__typename === 'ProfileDataMissingError',
    isUserConfirmed: (context, event) => event.data.userConfirmed === false,
    isSignup: (context) => context.Navigator.location.pathname === '/signup',
    isUserNotFoundAndEmail: (context, event) =>
      event.data.message === 'User does not exist.' &&
      !!context.username?.includes('@'),
  },
};

export default options;
